import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export function useProductStats() {
  return useQuery({
    queryKey: ['products', 'stats'],
    queryFn: async () => (await api.get('/api/products/stats')).data,
    staleTime: 30_000,
  });
}

export function useProducts(params) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
  const qs = new URLSearchParams(clean).toString();
  return useQuery({
    queryKey: ['products', 'list', clean],
    queryFn: async () => (await api.get(`/api/products?${qs}`)).data,
    keepPreviousData: true,
  });
}

// SKU catalog -> Xero Items sync (proxied to the tech server).
export function useXeroItemsStatus() {
  return useQuery({
    queryKey: ['xero', 'items-status'],
    queryFn: async () => (await api.get('/api/xero/items-status')).data,
    staleTime: 30_000,
  });
}

export function useSyncXero() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/xero/sync-items', body || {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['xero'] });
    },
  });
}

// Push a single SKU to Xero (per-row button). Takes the product id.
export function useSyncXeroItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/xero/sync-item/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['xero'] });
    },
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => (await api.patch(`/api/products/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useClearReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/products/${id}/clear-review`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

// The storeroom catalogue: everything on the monthly count sheet, with its
// supplier quotes. Returns the unorderable ones too — items nobody has priced,
// and items whose quotes disagree on the unit — because those are the ones
// somebody has to do something about.
export function useConsumableCatalog(params) {
  const qs = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== '' && v != null)).toString();
  return useQuery({
    queryKey: ['products', 'consumables', qs],
    queryFn: async () => (await api.get(`/api/products/consumables${qs ? `?${qs}` : ''}`)).data,
    staleTime: 30000,
  });
}
